import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, FileText, MessageSquare, Send, GraduationCap, DollarSign, CalendarDays } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Stagger, staggerItem } from "../components/Motion";

const steps = [
  { icon: FileText, title: "Apply online", body: "Complete the Heritage online application — it takes about 15 minutes." },
  { icon: MessageSquare, title: "Speak with admissions", body: "We'll book a friendly call to understand your goals and answer questions." },
  { icon: Send, title: "Submit documents", body: "Upload transcripts, ID and any English-language documentation." },
  { icon: CheckCircle2, title: "Receive your offer", body: "Most decisions are returned within 5–10 business days." },
];

const requirements = [
  "Ontario Secondary School Diploma (OSSD) or equivalent",
  "Mature students (19+) may apply with relevant work or life experience",
  "English language proficiency (IELTS 6.0 or equivalent for international students)",
  "Government-issued photo ID",
];

const tuitionItems = [
  { label: "Domestic tuition", value: "Available on request" },
  { label: "International tuition", value: "Available on request" },
  { label: "Application fee", value: "Available on request" },
  { label: "Scholarships", value: "Founding-year awards" },
];

const intakes = [
  { term: "Winter 2026", date: "January 2026", status: "Now accepting applications" },
  { term: "Spring 2026", date: "May 2026", status: "Applications open" },
  { term: "Fall 2026", date: "September 2026", status: "Early applications welcome" },
];

const Admissions = () => {
  return (
    <div data-testid="admissions-page">
      <PageHeader
        kicker="Admissions"
        title="Your path to Heritage starts here."
        subtitle="A simple, supportive process designed to help you take the next step with confidence."
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/contact" className="btn-primary" data-testid="admissions-talk-to-us">Talk to admissions</Link>
          <a href="mailto:info@hccbt.com" className="btn-secondary" data-testid="admissions-email-us">Email us</a>
        </div>
      </PageHeader>

      {/* Steps */}
      <section className="hccbt-container section pt-0">
        <span className="overline">How to apply</span>
        <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#0F172A] leading-tight">Four steps to enrolment</h2>
        <Stagger className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              variants={staggerItem}
              whileHover={{ y: -6 }}
              className="card-soft card-lift p-6"
              data-testid={`admissions-step-${i + 1}`}
            >
              <div className="flex items-center gap-3">
                <motion.div
                  whileHover={{ rotate: 8, scale: 1.08 }}
                  transition={{ type: "spring", stiffness: 240, damping: 14 }}
                  className="w-10 h-10 rounded-full bg-[#87CEEB]/25 flex items-center justify-center text-[#0F172A]"
                >
                  <s.icon size={18} />
                </motion.div>
                <span className="font-display text-2xl text-[#6B9AC4]">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-display text-xl mt-5 text-[#0F172A]">{s.title}</h3>
              <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </Stagger>
      </section>

      {/* Requirements & Tuition */}
      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="hccbt-container section grid lg:grid-cols-2 gap-10">
          <div className="card-soft p-10">
            <div className="w-12 h-12 rounded-full bg-[#87CEEB]/25 flex items-center justify-center text-[#0F172A]">
              <GraduationCap size={22} />
            </div>
            <h3 className="font-display text-2xl mt-6 text-[#0F172A]">Admission Requirements</h3>
            <ul className="mt-5 space-y-3">
              {requirements.map((r) => (
                <li key={r} className="flex items-start gap-3 text-[#334155]">
                  <CheckCircle2 size={18} className="text-[#6B9AC4] mt-0.5" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-soft p-10">
            <div className="w-12 h-12 rounded-full bg-[#87CEEB]/25 flex items-center justify-center text-[#0F172A]">
              <DollarSign size={22} />
            </div>
            <h3 className="font-display text-2xl mt-6 text-[#0F172A]">Tuition & Fees</h3>
            <div className="mt-5 divide-y divide-[#E2E8F0]">
              {tuitionItems.map((t) => (
                <div key={t.label} className="flex justify-between py-3 text-sm">
                  <span className="text-[#475569]">{t.label}</span>
                  <span className="font-semibold text-[#0F172A]">{t.value}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#64748B] mt-4">Final tuition and fees vary by program and citizenship status. Contact admissions for the current fee schedule.</p>
          </div>
        </div>
      </section>

      {/* Intakes */}
      <section className="hccbt-container section">
        <span className="overline">Upcoming Intakes</span>
        <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#0F172A] leading-tight">When can you start?</h2>
        <Stagger className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {intakes.map((i) => (
            <motion.div
              key={i.term}
              variants={staggerItem}
              whileHover={{ y: -6 }}
              className="card-soft card-lift p-6"
              data-testid={`intake-${i.term.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-3">
                <CalendarDays size={20} className="text-[#6B9AC4]" />
                <span className="font-display text-2xl text-[#0F172A]">{i.term}</span>
              </div>
              <p className="text-sm text-[#475569] mt-3">{i.date}</p>
              <p className="text-sm font-semibold text-[#6B9AC4] mt-3">{i.status}</p>
            </motion.div>
          ))}
        </Stagger>
      </section>
    </div>
  );
};

export default Admissions;
